import type { PaymentFailureCode, PaymentFailureReason } from 'paintscout'

export type PaymentFailureAudience = 'contractor' | 'customer'

const GENERIC_DECLINE = 'The payment was declined. Please try a different payment method or contact us.'

// Every string here is also a translation key — add new ones to TranslationProvider/locales/{es,fr}.json
const FAILURE_TEXT: Record<PaymentFailureCode, string> = {
  insufficient_funds: 'The payment was declined for insufficient funds.',
  card_declined: 'The payment was declined by the card issuer.',
  expired_card: 'The card has expired.',
  incorrect_cvc: "The card's security code is incorrect.",
  incorrect_number: 'The card number is incorrect.',
  incorrect_address: 'The billing address or postal code does not match the card.',
  card_not_supported: 'This payment method is not supported.',
  debit_not_allowed: 'Debit cards are not accepted for this payment.',
  limit_exceeded: 'The payment exceeds a limit on the account.',
  suspected_fraud: 'The card issuer declined the payment as lost, stolen, restricted or suspected fraud.',
  account_closed: 'The account is closed.',
  invalid_account: 'The bank account details are not valid.',
  authorization_revoked: 'The account holder stopped or did not authorize this payment.',
  processing_error: 'The payment could not be processed. Please try again.',
  canceled_by_user: 'The payment was canceled.',
  unknown: 'The payment processor did not give a reason.'
}

// Customers never see why a card was flagged (Stripe's guidance for fraud-type declines)
const CUSTOMER_OVERRIDES: Partial<Record<PaymentFailureCode, string>> = {
  card_declined: GENERIC_DECLINE,
  suspected_fraud: GENERIC_DECLINE,
  unknown: 'The payment failed. Please try a different payment method or contact us.'
}

// 'closed_account' → 'Closed account'
const toReadable = (providerCode: string) => {
  const words = providerCode.replaceAll('_', ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Display text for a failure reason. Mapped codes return an English sentence that is also a translation key, so wrap
 * the result in `__()`; untranslated provider text passes through `__()` unchanged.
 *
 * Contractors, first match wins — an ugly meaningful message beats a generic one:
 * 1. the sentence for a mapped code
 * 2. the raw provider message
 * 3. the provider code made readable ('closed_account' → 'Closed account')
 * 4. a generic sentence, only when there is nothing else
 *
 * Customers get the sentence for a mapped code, else the generic sentence. They never see provider text.
 */
export function getPaymentFailureText(
  reason: Pick<PaymentFailureReason, 'code' | 'providerCode' | 'message'> | null | undefined,
  audience: PaymentFailureAudience
): string {
  const code = reason?.code ?? 'unknown'
  if (audience === 'customer') {
    return CUSTOMER_OVERRIDES[code] ?? FAILURE_TEXT[code] ?? CUSTOMER_OVERRIDES.unknown
  }
  if (code !== 'unknown' && FAILURE_TEXT[code]) return FAILURE_TEXT[code]
  if (reason?.message) return reason.message
  if (reason?.providerCode) return toReadable(reason.providerCode)
  return FAILURE_TEXT.unknown
}
